/**
 * One validator finding, rendered the same way wherever it appears.
 *
 * `fix_hint` is always shown when present. A finding that says what is wrong
 * without saying what to do sends the operator back to the YAML to guess, and
 * guessing at a fleet config file is how the next finding gets created.
 */

import { Link } from 'react-router-dom';

import { SeverityBadge } from './Badge';
import type { FindingResponse } from '../types/api';

export function FindingItem({
  finding,
  showCenter = true,
}: {
  finding: FindingResponse;
  showCenter?: boolean;
}) {
  return (
    <li className="finding">
      <SeverityBadge severity={finding.severity} small />
      <div className="finding__body">
        <div className="finding__meta">
          <span className="mono">{finding.code}</span>
          {showCenter && finding.center_slug ? (
            <>
              <span aria-hidden="true">·</span>
              <Link to={`/centers/${finding.center_slug}`}>
                {finding.center_slug}
              </Link>
            </>
          ) : null}
          {finding.field ? (
            <>
              <span aria-hidden="true">·</span>
              <span className="mono">{finding.field}</span>
            </>
          ) : null}
        </div>
        <p className="finding__message">{finding.message}</p>
        {finding.fix_hint ? (
          <p className="finding__hint">
            <span className="finding__hint-label">Fix:</span> {finding.fix_hint}
          </p>
        ) : null}
      </div>
    </li>
  );
}

export function FindingList({
  findings,
  showCenter = true,
  label,
}: {
  findings: FindingResponse[];
  showCenter?: boolean;
  label?: string;
}) {
  return (
    <ul
      style={{ listStyle: 'none', margin: 0, padding: 0 }}
      aria-label={label}
    >
      {findings.map((finding, index) => (
        <FindingItem
          key={`${finding.code}-${finding.center_slug ?? ''}-${finding.field ?? ''}-${index}`}
          finding={finding}
          showCenter={showCenter}
        />
      ))}
    </ul>
  );
}
